import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../../hooks/redux';
import { FormInputText } from '../../../../inputs/FormInputText';
import { ButtonClassNames, ModalVariants, InputRegexPattern, InputTextFormat } from '../../../../../models/Common';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { ApiError } from '../../../../../models/ApiError';
import { modalSlice } from '../../../../../store/reducers/ModalSlice';
import { iptvApi } from '../../../../../services/apiServices/user/iptv';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';
import { getErrorMessage } from '../../../../../utils';

interface EditBrizTvPhoneProps {
  accountId: string | number;
  handleClose: () => void;
}

interface IEditBrizTvPhoneForm {
  phoneNumber: string;
  currentPassword: string;
}

export const EditBrizTvPhone: FC<EditBrizTvPhoneProps> = ({ accountId, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IEditBrizTvPhoneForm>();
  const { handleSubmit, setError } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editBrizTv, { isLoading }] = iptvApi.useEditBrizTvMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleEditPhone = async ({ phoneNumber, currentPassword }: IEditBrizTvPhoneForm) => {
    try {
      await editBrizTv({ phone: phoneNumber, accountId, currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.phoneBrizTvSuccessfullyChanged'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IEditBrizTvPhoneForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IEditBrizTvPhoneForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element === 'currentPassword' ? element : 'phoneNumber', {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.changingPhoneNumber')}</article>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleEditPhone)}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />

          <FormInputText
            name="phoneNumber"
            className="primary"
            label={t('input.phone')}
            isRequired
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
            type="tel"
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.save')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
